import { useQuery } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import gql from 'graphql-tag';
import Game from '../_types/Game';

export enum GameCarouselType {
  current = 0,
  future = 1,
  past = 2,
}

export enum ScopeType {
  person = 'person',
  circle = 'circle',
  all = 'noscope',
}

const beginningOfTimeEpoch = 0;
const endOfTimeEpoch = 8640000000000;

const getLeaderboardsQuery = /* GraphQL */ `
  query GetLeaderboards($request: AWSJSON!) {
    getLeaderboards(request: $request)
  }
`;

const listGamesQuery = (includeScope: boolean) => gql`
  query MyQuery(
    $startDateEpoch: [Float]!
    $orgID: ID!
    $endDateEpoch: [Float]!
    $nextToken: String
    ${includeScope ? '$scope: MISSION_SCOPE' : ''}
  ) {
    listMissionsByOrgStartDateEpoch(
      startDateEpoch: { between: $startDateEpoch }
      orgID: $orgID
      filter: {
        type: { ne: quest_mission }
        endDateEpoch: { between: $endDateEpoch }
        isDeleted: { eq: false }
        ${includeScope ? 'scope: { eq: $scope }' : ''}
      }
      limit: 120
      nextToken: $nextToken
    ) {
      items {
        id
        name
        startDate
        endDate
        startDateEpoch
        endDateEpoch
        imageName
        description
        createdAt
        type
        flexFields
      }
      nextToken
    }
  }
`;

const fetchGames = async (type: GameCarouselType, scope: ScopeType) => {
  const nowEpoch = Date.now() / 1000;
  let startDateRange: [number, number];
  let endDateRange: [number, number];

  switch (type) {
    case GameCarouselType.current:
      startDateRange = [beginningOfTimeEpoch, nowEpoch];
      endDateRange = [nowEpoch, endOfTimeEpoch];
      break;
    case GameCarouselType.future:
      startDateRange = [nowEpoch, endOfTimeEpoch];
      endDateRange = [nowEpoch, endOfTimeEpoch];
      break;
    case GameCarouselType.past:
      startDateRange = [beginningOfTimeEpoch, nowEpoch];
      endDateRange = [beginningOfTimeEpoch, nowEpoch];
      break;
  }

  const includeScope = scope === ScopeType.person || scope === ScopeType.circle;
  let nextToken: string | null = null;
  let allMissions: any[] = [];

  do {
    const query = listGamesQuery(includeScope);
    const variables = {
      startDateEpoch: startDateRange,
      endDateEpoch: endDateRange,
      // @ts-ignore
      orgID: global?.me?.orgID,
      nextToken,
      ...(includeScope && { scope }),
    };

    const res = (await API.graphql({
      // @ts-ignore
      query,
      variables,
    })) as {
      data: {
        listMissionsByOrgStartDateEpoch: {
          items: any[];
          nextToken: string | null;
        };
      };
    };

    const { items, nextToken: newNextToken } =
      res.data.listMissionsByOrgStartDateEpoch;

    // Fallback client-side filter: drop incentive child missions if present
    const filteredItems = (items || []).filter((item) => {
      try {
        const missionType = item.type;
        console.log('missionType', missionType);
        if (missionType === 'child_incentive') return false;
        return true;
      } catch (e) {
        return true;
      }
    });
    console.log('filteredItems', filteredItems);
    allMissions = [...allMissions, ...filteredItems];
    nextToken = newNextToken;
  } while (nextToken);

  console.log('allMissions', allMissions);
  return allMissions.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
};

export const useGetGames = (type: GameCarouselType, scope: ScopeType) => {
  return useQuery({
    queryKey: ['games', { type, scope }],
    queryFn: () => fetchGames(type, scope),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};

export const useGetSingleGame = (gameId: string) => {
  return useQuery({
    queryKey: ['game', gameId],
    queryFn: async () => {
      const response: any = await API.graphql({
        query: getLeaderboardsQuery,
        variables: {
          request: JSON.stringify({
            missionID: gameId,
          }),
        },
      });

      const jsonRes = JSON.parse(response?.data?.getLeaderboards) as {
        Items: Game[];
      };

      return jsonRes.Items?.[0];
    },
  });
};
